import { __extends } from "tslib";
import { Observable } from "../Misc/observable.js";
/**
 * Represents a gamepad control stick position
 */
var StickValues = /** @class */ (function () {
    /**
     * Initializes the gamepad x and y control stick values
     * @param x The x component of the gamepad control stick value
     * @param y The y component of the gamepad control stick value
     */
    function StickValues(
    /**
     * The x component of the control stick
     */
    x, 
    /**
     * The y component of the control stick
     */
    y) {
        this.x = x;
        this.y = y;
    }
    return StickValues;
}());
export { StickValues };
/**
 * Represents a gamepad
 */
var Gamepad = /** @class */ (function () {
    /**
     * Initializes the gamepad
     * @param id The id of the gamepad
     * @param index The index of the gamepad
     * @param browserGamepad The browser gamepad
     * @param leftStickX The x component of the left joystick
     * @param leftStickY The y component of the left joystick
     * @param rightStickX The x component of the right joystick
     * @param rightStickY The y component of the right joystick
     */
    function Gamepad(
    /**
     * The id of the gamepad
     */
    id, 
    /**
     * The index of the gamepad
     */
    index, 
    /**
     * The browser gamepad
     */
    browserGamepad, leftStickX, leftStickY, rightStickX, rightStickY) {
        if (leftStickX === void 0) { leftStickX = 0; }
        if (leftStickY === void 0) { leftStickY = 1; }
        if (rightStickX === void 0) { rightStickX = 2; }
        if (rightStickY === void 0) { rightStickY = 3; }
        this.id = id;
        this.index = index;
        this.browserGamepad = browserGamepad;
        this._leftStick = { x: 0, y: 0 };
        this._rightStick = { x: 0, y: 0 };
        /** @hidden */
        this._isConnected = true;
        /**
         * Specifies whether the left control stick should be Y-inverted
         */
        this._invertLeftStickY = false;
        this.type = Gamepad.GAMEPAD;
        this._leftStickAxisX = leftStickX;
        this._leftStickAxisY = leftStickY;
        this._rightStickAxisX = rightStickX;
        this._rightStickAxisY = rightStickY;
        if (this.browserGamepad.axes.length >= 2) {
            this._leftStick = { x: this.browserGamepad.axes[this._leftStickAxisX], y: this.browserGamepad.axes[this._leftStickAxisY] };
        }
        if (this.browserGamepad.axes.length >= 4) {
            this._rightStick = { x: this.browserGamepad.axes[this._rightStickAxisX], y: this.browserGamepad.axes[this._rightStickAxisY] };
        }
    }
    Object.defineProperty(Gamepad.prototype, "isConnected", {
        /**
         * Specifies if the gamepad has been connected
         */
        get: function () {
            return this._isConnected;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Callback triggered when the left joystick has changed
     * @param callback
     */
    Gamepad.prototype.onleftstickchanged = function (callback) {
        this._onleftstickchanged = callback;
    };
    /**
     * Callback triggered when the right joystick has changed
     * @param callback
     */
    Gamepad.prototype.onrightstickchanged = function (callback) {
        this._onrightstickchanged = callback;
    };
    Object.defineProperty(Gamepad.prototype, "leftStick", {
        /**
         * Gets the left joystick
         */
        get: function () {
            return this._leftStick;
        },
        /**
         * Sets the left joystick values
         */
        set: function (newValues) {
            if (this._onleftstickchanged && (this._leftStick.x !== newValues.x || this._leftStick.y !== newValues.y)) {
                this._onleftstickchanged(newValues);
            }
            this._leftStick = newValues;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Gamepad.prototype, "rightStick", {
        /**
         * Gets the right joystick
         */
        get: function () {
            return this._rightStick;
        },
        /**
         * Sets the right joystick value
         */
        set: function (newValues) {
            if (this._onrightstickchanged && (this._rightStick.x !== newValues.x || this._rightStick.y !== newValues.y)) {
                this._onrightstickchanged(newValues);
            }
            this._rightStick = newValues;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Updates the gamepad joystick positions
     */
    Gamepad.prototype.update = function () {
        if (this._leftStick) {
            this.leftStick = { x: this.browserGamepad.axes[this._leftStickAxisX], y: this.browserGamepad.axes[this._leftStickAxisY] };
            if (this._invertLeftStickY) {
                this.leftStick.y *= -1;
            }
        }
        if (this._rightStick) {
            this.rightStick = { x: this.browserGamepad.axes[this._rightStickAxisX], y: this.browserGamepad.axes[this._rightStickAxisY] };
        }
    };
    /**
     * Disposes the gamepad
     */
    Gamepad.prototype.dispose = function () { };
    /**
     * Represents a gamepad controller
     */
    Gamepad.GAMEPAD = 0;
    /**
     * Represents a generic controller
     */
    Gamepad.GENERIC = 1;
    /**
     * Represents an XBox controller
     */
    Gamepad.XBOX = 2;
    /**
     * Represents a pose-enabled controller
     */
    Gamepad.POSE_ENABLED = 3;
    /**
     * Represents an Dual Shock controller
     */
    Gamepad.DUALSHOCK = 4;
    return Gamepad;
}());
export { Gamepad };
/**
 * Represents a generic gamepad
 */
var GenericPad = /** @class */ (function (_super) {
    __extends(GenericPad, _super);
    /**
     * Initializes the generic gamepad
     * @param id The id of the generic gamepad
     * @param index The index of the generic gamepad
     * @param browserGamepad The browser gamepad
     */
    function GenericPad(id, index, browserGamepad) {
        var _this = _super.call(this, id, index, browserGamepad) || this;
        /**
         * Observable triggered when a button has been pressed
         */
        _this.onButtonDownObservable = new Observable();
        /**
         * Observable triggered when a button has been released
         */
        _this.onButtonUpObservable = new Observable();
        _this.type = Gamepad.GENERIC;
        _this._buttons = new Array(browserGamepad.buttons.length);
        return _this;
    }
    /**
     * Callback triggered when a button has been pressed
     * @param callback Called when a button has been pressed
     */
    GenericPad.prototype.onbuttondown = function (callback) {
        this._onbuttondown = callback;
    };
    /**
     * Callback triggered when a button has been released
     * @param callback Called when a button has been released
     */
    GenericPad.prototype.onbuttonup = function (callback) {
        this._onbuttonup = callback;
    };
    GenericPad.prototype._setButtonValue = function (newValue, currentValue, buttonIndex) {
        if (newValue !== currentValue) {
            if (newValue === 1) {
                if (this._onbuttondown) {
                    this._onbuttondown(buttonIndex);
                }
                this.onButtonDownObservable.notifyObservers(buttonIndex);
            }
            if (newValue === 0) {
                if (this._onbuttonup) {
                    this._onbuttonup(buttonIndex);
                }
                this.onButtonUpObservable.notifyObservers(buttonIndex);
            }
        }
        return newValue;
    };
    /**
     * Updates the generic gamepad
     */
    GenericPad.prototype.update = function () {
        _super.prototype.update.call(this);
        for (var index = 0; index < this._buttons.length; index++) {
            this._buttons[index] = this._setButtonValue(this.browserGamepad.buttons[index].value, this._buttons[index], index);
        }
    };
    /**
     * Disposes the generic gamepad
     */
    GenericPad.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this.onButtonDownObservable.clear();
        this.onButtonUpObservable.clear();
    };
    return GenericPad;
}(Gamepad));
export { GenericPad };
//# sourceMappingURL=gamepad.js.map